import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  FormControlLabel,
  Checkbox,
  Divider,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import ProductService, { Product } from '../../services/product.service';
import { NumberTextField } from '../../components/common';

const ProductForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadedImages, setUploadedImages] = useState<any[]>([]);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  const [formData, setFormData] = useState<Product>({
    name: '',
    sku: '',
    price: 0,
    unit: 'piece',
    currency: 'OMR',
    isActive: true,
    minStockLevel: 0,
  });

  useEffect(() => {
    if (isEdit && id) {
      fetchProduct(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchProduct = async (productId: number) => {
    try {
      setLoading(true);
      const response = await ProductService.getOne(productId);
      const productData = response.data?.data;
      if (productData) {
        const attrs = productData.attributes;
        setFormData({
          ...attrs,
          category: attrs.category?.data?.id,
        });
        
        // Set existing images
        if (attrs?.images?.data) {
          setUploadedImages(attrs.images.data);
        }
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      setError('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setImageFiles((prev) => [...prev, ...files]);

      // Create previews
      files.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews((prev) => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeNewImage = (index: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const removeUploadedImage = (imageId: number) => {
    setUploadedImages((prev) => prev.filter((img) => img.id !== imageId));
  };

  const uploadImages = async (): Promise<number[]> => {
    if (imageFiles.length === 0) return [];

    const imageIds: number[] = [];
    const API_URL = (process.env.REACT_APP_API_URL || 'https://darb.aiastra.io').replace(/\/$/, '');

    for (const file of imageFiles) {
      const formData = new FormData();
      formData.append('files', file);

      try {
        const response = await fetch(`${API_URL}/api/upload`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: formData,
        });

        const data = await response.json();
        if (data && data[0]) {
          imageIds.push(data[0].id);
        }
      } catch (error) {
        console.error('Error uploading image:', error);
      }
    }

    return imageIds;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Upload new images
      const newImageIds = await uploadImages();

      // Combine with existing images
      const existingImageIds = uploadedImages.map((img) => img.id);
      const allImageIds = [...existingImageIds, ...newImageIds];

      const priceNum =
        typeof formData.price === 'number'
          ? formData.price
          : parseFloat(String(formData.price).trim()) || 0;
      const costVal = formData.cost;
      const costNum =
        costVal != null && String(costVal).trim() !== ''
          ? typeof costVal === 'number'
            ? costVal
            : parseFloat(String(costVal).trim())
          : undefined;

      // Build payload with only schema fields; send price/cost as strings for Strapi decimal
      const submitData: Record<string, unknown> = {
        name: formData.name,
        description: formData.description ?? undefined,
        sku: formData.sku,
        barcode: formData.barcode || undefined,
        category: formData.category || undefined,
        price: String(priceNum),
        cost: costNum !== undefined && !isNaN(costNum) ? String(costNum) : undefined,
        currency: formData.currency || 'OMR',
        unit: formData.unit || 'piece',
        minStockLevel:
          typeof formData.minStockLevel === 'number'
            ? formData.minStockLevel
            : parseInt(String(formData.minStockLevel || 0), 10) || 0,
        isActive: formData.isActive !== false,
        images: allImageIds.length > 0 ? allImageIds : undefined,
      };
      if (formData.weight != null && String(formData.weight).trim() !== '') {
        const w =
          typeof formData.weight === 'number'
            ? formData.weight
            : parseFloat(String(formData.weight));
        if (!isNaN(w)) submitData.weight = String(w);
      }
      if (formData.dimensions) submitData.dimensions = formData.dimensions;
      const supplierId =
        typeof formData.supplier === 'number'
          ? formData.supplier
          : (formData.supplier as any)?.data?.id ?? (formData.supplier as any)?.id;
      if (supplierId) submitData.supplier = supplierId;

      if (isEdit && id) {
        await ProductService.update(parseInt(id), submitData as unknown as Partial<Product>);
        setSuccess('Product updated successfully!');
      } else {
        await ProductService.create(submitData as unknown as Product);
        setSuccess('Product created successfully!');
      }

      setTimeout(() => {
        navigate('/products');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Product' : 'Create Product'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* Basic Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  required
                  label="Product Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  required
                  label="SKU"
                  name="sku"
                  value={formData.sku}
                  onChange={handleChange}
                  disabled={isEdit}
                  helperText="Unique product identifier"
                />
              </Stack>

              <TextField
                fullWidth
                label="Barcode"
                name="barcode"
                value={formData.barcode || ''}
                onChange={handleChange}
                helperText="Optional barcode number"
              />

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                name="description"
                value={formData.description || ''}
                onChange={handleChange}
                placeholder="Detailed product description..."
              />
            </Stack>
          </Paper>

          {/* Pricing */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Pricing & Stock
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <NumberTextField
                  fullWidth
                  required
                  label="Selling Price"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  onValueChange={(val) => setFormData((prev) => ({ ...prev, price: val }))}
                  inputProps={{ step: '0.01', min: '0' }}
                />
                <NumberTextField
                  fullWidth
                  label="Cost Price"
                  name="cost"
                  value={formData.cost ?? ''}
                  onChange={handleChange}
                  onValueChange={(val) => setFormData((prev) => ({ ...prev, cost: val }))}
                  inputProps={{ step: '0.01', min: '0' }}
                  helperText="Purchase/manufacturing cost"
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  select
                  label="Currency"
                  name="currency"
                  value={formData.currency || 'OMR'}
                  onChange={handleChange}
                >
                  <MenuItem value="OMR">OMR - Omani Rial</MenuItem>
                  <MenuItem value="USD">USD - US Dollar</MenuItem>
                  <MenuItem value="EUR">EUR - Euro</MenuItem>
                  <MenuItem value="GBP">GBP - British Pound</MenuItem>
                  <MenuItem value="SAR">SAR - Saudi Riyal</MenuItem>
                  <MenuItem value="AED">AED - UAE Dirham</MenuItem>
                </TextField>

                <TextField
                  fullWidth
                  select
                  label="Unit"
                  name="unit"
                  value={formData.unit || 'piece'}
                  onChange={handleChange}
                >
                  <MenuItem value="piece">Piece</MenuItem>
                  <MenuItem value="kg">Kilogram (kg)</MenuItem>
                  <MenuItem value="liter">Liter</MenuItem>
                  <MenuItem value="meter">Meter</MenuItem>
                  <MenuItem value="box">Box</MenuItem>
                  <MenuItem value="pack">Pack</MenuItem>
                </TextField>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <NumberTextField
                  fullWidth
                  label="Minimum Stock Level"
                  name="minStockLevel"
                  value={formData.minStockLevel || 0}
                  onChange={handleChange}
                  inputProps={{ min: '0' }}
                  allowDecimal={false}
                  helperText="Alert when stock falls below this"
                />
                <NumberTextField
                  fullWidth
                  label="Weight (kg)"
                  name="weight"
                  value={formData.weight || ''}
                  onChange={handleChange}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Stack>

              <TextField
                fullWidth
                label="Dimensions"
                name="dimensions"
                value={formData.dimensions || ''}
                onChange={handleChange}
                placeholder="e.g., 10cm x 20cm x 5cm"
              />
            </Stack>
          </Paper>

          {/* Images */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Product Images
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={2}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
              >
                Upload Images
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  onChange={handleImageSelect}
                />
              </Button>

              {/* Existing Uploaded Images */}
              {uploadedImages.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Current Images
                  </Typography>
                  <ImageList cols={4} gap={8}>
                    {uploadedImages.map((image) => (
                      <ImageListItem key={image.id}>
                        <img
                          src={`${(process.env.REACT_APP_API_URL || 'https://darb.aiastra.io').replace(/\/$/, '')}${image.attributes?.url}`}
                          alt={image.attributes?.name}
                          style={{ height: 150, objectFit: 'cover' }}
                        />
                        <ImageListItemBar
                          actionIcon={
                            <IconButton
                              sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                              onClick={() => removeUploadedImage(image.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          }
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Box>
              )}

              {/* New Image Previews */}
              {imagePreviews.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    New Images to Upload
                  </Typography>
                  <ImageList cols={4} gap={8}>
                    {imagePreviews.map((preview, index) => (
                      <ImageListItem key={index}>
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          style={{ height: 150, objectFit: 'cover' }}
                        />
                        <ImageListItemBar
                          actionIcon={
                            <IconButton
                              sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                              onClick={() => removeNewImage(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          }
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Box>
              )}
            </Stack>
          </Paper>

          {/* Status */}
          <Paper sx={{ p: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  name="isActive"
                  checked={formData.isActive !== false}
                  onChange={handleChange}
                />
              }
              label="Product is Active"
            />
          </Paper>

          {/* Action Buttons */}
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/products')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : isEdit ? 'Update Product' : 'Create Product'}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </form>
    </Box>
  );
};

export default ProductForm;
